import { memo, useState, useEffect, useCallback, useMemo, useRef, MouseEvent } from 'react';
import { LOGO_DARK } from '@/lib/constants';
import { useNavigation } from '@/contexts/NavigationContext';
import { useLanguage } from '@/contexts/LanguageContext';


/** Real <a> that intercepts clicks for SPA transition but remains crawlable */
const NavAnchor = memo(({ to, children, className, onClick: extraOnClick }: { to: string; children: React.ReactNode; className?: string; onClick?: () => void }) => {
  const { navigateTo } = useNavigation();
  const handleClick = useCallback((e: MouseEvent<HTMLAnchorElement>) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey) return;
    e.preventDefault();
    extraOnClick?.();
    navigateTo(to);
  }, [to, navigateTo, extraOnClick]);
  return <a href={to} onClick={handleClick} className={className}>{children}</a>;
});
NavAnchor.displayName = 'NavAnchor';

export const Navbar = memo(() => {
  const { navigateTo, currentPath, triggerCurtain } = useNavigation();
  const { language, setLanguage, t } = useLanguage();

  const workLinks = useMemo(
    () => [
      { name: t.services.eventbau.title, path: '/eventbau' },
      { name: t.services.aktivierung.title, path: '/aktivierung' },
      { name: t.services.werbetechnik.title, path: '/werbetechnik' },
      { name: t.services.posm.title, path: '/posm' },
    ],
    [t],
  );

  const toggleLanguage = useCallback(() => {
    triggerCurtain(() => {
      setLanguage(language === 'de' ? 'en' : 'de');
    });
  }, [triggerCurtain, setLanguage, language]);
  const [scrolled, setScrolled] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const isTransitioning = useRef(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrolled(window.scrollY > 50);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: globalThis.MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsMobileDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const isOnWorkPage = workLinks.some((l) => l.path === currentPath);
    if (!isOnWorkPage) {
      setIsExpanded(false);
    } else {
      setIsExpanded(true);
      setIsVisible(true);
    }
    setIsMobileDropdownOpen(false);
  }, [currentPath, workLinks]);

  const handleOpenWork = useCallback(() => {
    if (isExpanded || isTransitioning.current) return;
    isTransitioning.current = true;
    setIsVisible(false);
    setTimeout(() => {
      setIsExpanded(true);
      setTimeout(() => {
        setIsVisible(true);
        setTimeout(() => { isTransitioning.current = false; }, 300);
      }, 50); 
    }, 300); 
  }, [isExpanded]);

  const handleMobileWorkClick = () => {
    setIsMobileDropdownOpen(!isMobileDropdownOpen);
  };

  const closeMobileDropdown = useCallback(() => {
    setIsMobileDropdownOpen(false);
  }, []);

  const isWorkPage = workLinks.some((l) => l.path === currentPath);

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 border-b bg-white text-black ${
      scrolled ? 'border-transparent py-3 md:py-4' : 'border-transparent py-4 md:py-6'
    }`}>
      <div className="w-full pl-4 pr-4 md:pl-6 md:pr-6 lg:pl-8 lg:pr-8 xl:pl-12 xl:pr-12 2xl:pl-16 2xl:pr-16 flex items-center justify-between gap-4 md:gap-6 lg:gap-8">
        {/* Logo */}
        <NavAnchor to="/" className="flex-shrink-0 flex items-center group">
          <img 
            src={LOGO_DARK} 
            alt="go4it.agency - Event Agentur Zürich Logo" 
            className="h-5 w-[46px] md:h-6 md:w-[56px] lg:h-7 lg:w-[65px] block"
            fetchPriority="high"
            width={65}
            height={28}
          />
        </NavAnchor>

        {/* Navigation Links */}
        <div 
          className={`flex-1 flex items-center justify-center gap-3 md:gap-5 lg:gap-8 xl:gap-12 transition-opacity duration-300 ease-in-out ${isVisible ? 'opacity-100' : 'opacity-0'}`}
        >
          <NavAnchor 
            to="/"
            className={`font-druk text-[9px] md:text-[11px] lg:text-xs xl:text-sm font-bold uppercase tracking-wide hover:text-brand transition-colors whitespace-nowrap ${currentPath === '/' ? 'text-brand' : ''}`}
          >
            {t.nav.home}
          </NavAnchor>

          {/* Desktop: Work links */}
          <div className="hidden md:flex items-center gap-3 lg:gap-5 xl:gap-7">
            {isExpanded ? (
              <>
                {workLinks.map((link) => (
                  <NavAnchor 
                    key={link.path} 
                    to={link.path}
                    className={`font-druk text-[9px] lg:text-[11px] font-bold uppercase tracking-widest hover:text-brand transition-colors relative group whitespace-nowrap ${currentPath === link.path ? 'text-brand' : ''}`}
                  >
                    {link.name}
                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-brand transition-all group-hover:w-full"></span>
                  </NavAnchor>
                ))}
              </>
            ) : (
              <button 
                onClick={handleOpenWork}
                className={`font-druk text-[11px] lg:text-xs xl:text-sm font-bold uppercase tracking-wide hover:text-brand transition-colors whitespace-nowrap ${isWorkPage ? 'text-brand' : ''}`}
              >
                {t.nav.work}
              </button>
            )}
          </div>

          {/* Mobile: Work dropdown */}
          <div className="md:hidden relative flex items-center" ref={dropdownRef}>
            <button 
              onClick={handleMobileWorkClick}
              className={`font-druk text-[9px] font-bold uppercase tracking-wide hover:text-brand transition-colors whitespace-nowrap ${isWorkPage ? 'text-brand' : ''}`}
            >
              {t.nav.work}
            </button>
            
            <div className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-white border border-black/10 shadow-lg z-[100] min-w-[180px] transition-all duration-200 origin-top ${
              isMobileDropdownOpen 
                ? 'opacity-100 scale-y-100 pointer-events-auto' 
                : 'opacity-0 scale-y-95 pointer-events-none'
            }`}>
              {workLinks.map((link) => (
                <NavAnchor 
                  key={link.path} 
                  to={link.path}
                  onClick={closeMobileDropdown}
                  className={`font-druk block w-full text-left px-4 py-3 text-xs font-bold uppercase tracking-widest hover:bg-gray-100 hover:text-brand transition-colors ${currentPath === link.path ? 'text-brand bg-gray-50' : ''}`}
                >
                  {link.name}
                </NavAnchor>
              ))}
            </div>
          </div>

          {/* About */}
          <NavAnchor 
            to="/about"
            className={`font-druk text-[9px] md:text-[11px] lg:text-xs xl:text-sm font-bold uppercase tracking-wide hover:text-brand transition-colors whitespace-nowrap ${currentPath === '/about' ? 'text-brand' : ''}`}
          >
            {t.nav.about}
          </NavAnchor>
        </div>

        {/* Right side: WhatsApp + Language Toggle */}
        <div className="flex-shrink-0 flex items-center gap-2 md:gap-3">
          
          <button
            onClick={toggleLanguage}
            className="font-druk text-[9px] md:text-[11px] lg:text-xs font-bold uppercase tracking-wide hover:text-brand transition-colors"
          >
            <span className={language === 'de' ? 'text-brand' : ''}>DE</span>
            <span className="mx-1">/</span>
            <span className={language === 'en' ? 'text-brand' : ''}>EN</span>
          </button>
        </div>
      </div>
    </nav>
  );
});

Navbar.displayName = 'Navbar';
